import React, { memo, useContext, useMemo, useState } from 'react';
import { DatePicker, MenuItem, RaisedButton } from 'material-ui';
import VesselAutoComplete from '../common/VesselAutoComplete';
import { fromUTC } from '../../helpers/dates';
import moment from 'moment';
import AddUpdateButtons from './AddUpdateButtons';
import styles from '../TcIndex/styles';
import { HIRE_TYPES_MAP } from '../../constants/hire';
import BasicSelector from '../TcIndex/Basic/BasicSelector';
import PortsAutoCompleteMulti from '../common/PortsAutoCompleteMulti';
import UserAutoComplete from '../common/UserAutoComplete';
import { TYPES_BY_CONSTRUCTION } from 'constants/vessel';
import { VesselDwtTypesContext } from './HiresPage';
import _ from 'lodash';
import withClearButton from '../common/HOC/withClearButton';

const VesselAutoCompleteWithClearButton = withClearButton(VesselAutoComplete);
const UserAutoCompleteWithClearButton = withClearButton(UserAutoComplete);
const DatePickerWithClearButton = withClearButton(DatePicker);

/**
 * 
 * @param {{
 *  filters: Filters,
 *  changeFilters: Function,
 *  clearFilters: Function,
 * }} param0 
 * @returns 
 */
function Filters({ filters, changeFilters, clearFilters }) {
    const [clear, setClear] = useState(false);
    const maxDate = new Date();
    const vesselDwtTypes = useContext(VesselDwtTypesContext);

    const selectVessel = (vessel) => {
        changeFilters({ vesselId: vessel._id });
    };

    const selectHireType = (hireType) => {
        changeFilters({ hireType });
    };

    const selectPort = (field, val) => {
        changeFilters({ [field]: val.map(v => v._id) });
    };

    const handleChangeDate = (field, val) => {
        changeFilters({ [field]: val });
    };

    const _clearFilters = () => {
        clearFilters();
        setClear(true);
    };

    const onUserSelected = (value) => {
        changeFilters({
            addedBy: value ? value.item._id : value,
        });
    };
    const dwtGroups = useMemo(
        () => 
            (filters.typeByPurpose ? vesselDwtTypes.filter(d => d.typeByPurpose.includes(filters.typeByPurpose)) : []), 
        [filters.typeByPurpose, vesselDwtTypes],
    );

    return (
        <React.Fragment>
            <div style={{ display: 'flex' }}>
                <div className="item" style={{ marginRight: '10px' }}>
                    <div className="row">
                        <div className="col-xs-3">
                            <VesselAutoCompleteWithClearButton 
                                selectValue={selectVessel}
                                hintText='Vessel'
                                shouldClearCustomValue={clear}
                                value={filters.vesselId}
                                onClear={() => {
                                    changeFilters({ vesselId: undefined });
                                    setClear(true);
                                }}
                            />
                        </div>
                        <div className="col-xs-2">
                            <BasicSelector 
                                value={filters.hireType}
                                dataList={HIRE_TYPES_MAP}
                                hintText="Hire type"
                                onChange={(e, _i, value) => selectHireType(value)}
                                style={{ fontSize: '13px', height: '48px' }}
                                labelStyle={{ padding: 0, top: 'unset' }}
                                floatingLabelStyle={{ top: '14px' }}
                                iconStyle={{ width: '40px' }}
                                fields={['name']}
                                multiple
                                customRender={
                                    HIRE_TYPES_MAP
                                        .map(item => (<MenuItem 
                                            key={item.field}
                                            value={item.field}
                                            primaryText={item.placeHolder}
                                        />))
                                }
                                selectionRenderer={values => (
                                    <div style={{ 
                                        maxWidth: 'calc(100% - 20px)', 
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    >
                                        {values.map(v => HIRE_TYPES_MAP.find(h => h.field === v).placeHolder).join(', ')}
                                    </div>
                                )}
                            />
                        </div>
                        <div className="col-xs-3">
                            <UserAutoCompleteWithClearButton 
                                onUserSelected={onUserSelected}
                                hintText='Added by (email)'
                                resetInput={clear}
                                value={filters.addedBy}
                                onClear={() => {
                                    changeFilters({ addedBy: undefined });
                                    setClear(true);
                                }}
                            />
                        </div>
                        {
                            [
                                { field: 'addedBefore', hintText: 'Added before' },
                                { field: 'addedAfter', hintText: 'Added After' },
                            ].map((v, i) => (
                                <div className="col-xs-2" key={`${v.field}_${i}`}>
                                    <DatePicker 
                                        autoOk
                                        maxDate={(() => {
                                            if (v.field === 'lay') {
                                                return filters.can ? new Date(filters.can) : maxDate;
                                            }
                                            return null;
                                        })()}
                                        minDate={(() => {
                                            if (v.field === 'can') {
                                                return filters.lay ? new Date(filters.lay) : undefined;
                                            }
                                            return null;
                                        })()}
                                        onChange={(__, date) => {
                                            v.field === 'addedAfter' && handleChangeDate(v.field, moment(date).utcOffset(0, true));
                                            v.field === 'addedBefore' && handleChangeDate(v.field, moment(date).utc().endOf('day').add(1, 'day'));
                                        }}
                                        value={filters[v.field] ? fromUTC(filters[v.field]) : undefined}
                                        inputStyle={{ fontSize: '13px', cursor: 'pointer', textAlign: 'center' }}
                                        textFieldStyle={{ width: 'unset' }}
                                        hintText={v.hintText}
                                        hintStyle={{ fontSize: '13px' }}
                                    />
                                </div>
                            ))
                        }
                    </div>
                </div>
                <div className="item">
                    <div className="row">
                        {
                            [
                                { field: 'lay', hintText: 'Lay' },
                                { field: 'can', hintText: 'Can' },
                            ].map((v, i) => (
                                <div className="col-xs-2" key={`${v.field}_${i}`}>
                                    <DatePickerWithClearButton 
                                        key={`${v.field}_${filters[v.field]}`}
                                        autoOk
                                        maxDate={(() => {
                                            if (v.field === 'lay') {
                                                return filters.can ? new Date(filters.can) : undefined;
                                            }
                                            return null;
                                        })()}
                                        minDate={(() => {
                                            if (v.field === 'can') {
                                                return filters.lay ? new Date(filters.lay) : undefined;
                                            }
                                            return null;
                                        })()}
                                        onChange={(__, date) => {
                                            v.field === 'lay' && handleChangeDate(v.field, moment(date).utcOffset(0, true));
                                            v.field === 'can' && handleChangeDate(v.field, moment(date).utc().endOf('day').add(1, 'day'));
                                        }}
                                        value={filters[v.field] ? fromUTC(filters[v.field]) : null}
                                        inputStyle={{ fontSize: '13px', cursor: 'pointer' }}
                                        textFieldStyle={{ width: 'unset' }}
                                        hintText={v.hintText}
                                        hintStyle={{ fontSize: '13px' }}
                                        clearValue={filters[v.field]} 
                                        onClear={() => {
                                            changeFilters({ [v.field]: undefined });
                                        }}
                                    />
                                </div>
                            ))
                        }
                        <div className="col-xs-2">
                            <RaisedButton 
                                label='Clear'
                                labelStyle={{ fontSize: '13px' }}
                                style={{ ...styles.buttons.style }}
                                onClick={_clearFilters}
                            />
                        </div>
                        <AddUpdateButtons 
                            style={{ ...styles.buttons.style }}
                            labelStyle={{ ...styles.buttons.labelStyle }}
                        />
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex' }}>
                <div className='item' style={{ marginRight: '10px' }}>
                    <div className='row'>
                        <div className='col-xs-3'>
                            <BasicSelector 
                                value={filters.vesselDwtGroup ? filters.vesselDwtGroup._id : ''}
                                hintText='DWT group'
                                dataList={[undefined, ...dwtGroups]}
                                onChange={(e, _i, value) => 
                                    changeFilters({ vesselDwtGroup: _.pick(dwtGroups.find(d => d._id === value), ['_id', 'min', 'max']) })}
                                style={{ fontSize: '13px', height: '48px' }}
                                labelStyle={{ padding: 0, top: 'unset' }}
                                iconStyle={{ width: '40px' }}
                                fields={['min', 'max']}
                                disabled={!filters.typeByPurpose}
                            />
                        </div>
                        <div className='col-xs-3'>
                            <BasicSelector 
                                value={filters.typeByConstruction}
                                dataList={TYPES_BY_CONSTRUCTION}
                                onChange={(e, _i, value) => changeFilters({ typeByConstruction: value })}
                                hintText='Type by construction'
                                style={{ fontSize: '13px', height: '48px' }}
                                labelStyle={{ padding: 0, top: 'unset' }}
                                iconStyle={{ width: '40px' }}
                            />
                        </div>
                        <div className='col-xs-3' style={{ paddingLeft: '20px' }}>
                            <BasicSelector 
                                value={filters.geared}
                                dataList={['', true, false]}
                                onChange={(e, _i, value) => changeFilters({ geared: value })}
                                hintText='Geared'
                                style={{ fontSize: '13px', height: '48px' }}
                                labelStyle={{ padding: 0, top: 'unset' }}
                                iconStyle={{ width: '40px' }}
                                customRender={
                                    ['', true, false]
                                        .map((item, index) => (<MenuItem 
                                            key={`geared_${index}`}
                                            value={item === '' ? undefined : item}
                                            primaryText={String(item)}
                                        />))
                                }
                            />
                        </div>
                        <div className='col-xs-3'>
                            <BasicSelector 
                                value={filters.maxGearsCapacity || ''}
                                dataList={[undefined, 1, 5, 10, 15, 20, 30, 35, 40]}
                                onChange={(e, _i, value) => changeFilters({ maxGearsCapacity: value })}
                                hintText='Max gear capacity'
                                style={{ fontSize: '13px', height: '48px' }}
                                labelStyle={{ padding: 0, top: 'unset' }}
                                iconStyle={{ width: '40px' }}
                            />
                        </div>
                    </div>
                </div>
                <div className="item">
                    <div className="row">
                        {
                            [
                                { field: 'mainRegionDeliveries', hintText: 'Main Region dels' },
                                { field: 'mainRegionRedeliveries', hintText: 'Main Region redels' },
                            ].map((v, i) => (
                                <div className="col-xs-4" key={v.field}>
                                    <PortsAutoCompleteMulti 
                                        key={`${v.field}_${i}`}
                                        customValue={filters[v.field] ? filters[v.field].name : ''}
                                        shouldClearCustomValue={clear}
                                        handleChangeCustomValue={() => {}}
                                        selectValue={e => selectPort(v.field, e)}
                                        floatingLabelStyle={{ display: 'none' }}
                                        hintText={v.hintText}
                                        inputStyle={{ fontSize: '13px' }}
                                        style={{ top: 'unset' }}
                                        menuStyle={{ width: '300px', maxHeight: '200px' }}
                                        
                                        textFieldStyle={{ height: '26px' }}
                                        withAreas
                                        mainRegions
                                        onlyAreas
                                        isInBalticIndex
                                    />
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>   
        </React.Fragment>
    );
}

/**
 * @typedef {Object} Filters
 * @property {string} [vesselId]
 * @property {import('constants/hire').HireType} [hireType]
 * @property {Date} [addedAfter]
 * @property {Date} [addedBefore]
 * @property {Date} [lay]
 * @property {Date} [can]
 * @property {string} [addedBy]
 * @property {string} [mainRegionDeliveries]
 * @property {string} [mainRegionRedeliveries]
 * @property {'MPP' | 'BULK'} [typeByPurpose]
 * @property {'TWEEN' | 'SID'} [typeByConstruction]
 * @property {any} [vesselDwtGroup]
 * @property {boolean | null} [geared]
 * @property {number} [maxGearsCapacity]
 */

export default memo(Filters, (prevProps, nextProps) => 
    _.isEqual(prevProps.filters, nextProps.filters));



// WEBPACK FOOTER //
// ./app/components/Hires/Filters.jsx